import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Dropdown} from "semantic-ui-react";
import './styles.scss';
import {apiPost} from "../../services/apiClient";
import uuidv4 from "uuid/v4";
import {toast} from "react-toastify";
import throttle from 'lodash.throttle';

const InputDropdown = (
    {
        className,
        name,
        nameOption,
        value,
        url,
        disabled,
        onChange,
        params,
        paramsForSearchFields,
        dependentFields,
        ignoredFields,
        searchErrorText,
        placeholder,
        error,
        /** Разрешить запрос с пустым text (первые N подсказок по индексу/городу и т.д.) */
        allowEmptySearch,
        maxCount = 10
    }
) => {
    const [options, setOptions] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [counter, setCounter] = useState(0);
    const [sessionKey] = useState(uuidv4());
    const valueRef = useRef(value);
    valueRef.current = value;

    let controller;

    const paramsScopeKey = useMemo(() => {
        if (!paramsForSearchFields || !params) {
            return '';
        }
        return paramsForSearchFields.map((pf) => {
            const v = params[pf[0]];
            return v == null ? '' : String(v).trim();
        }).join('\u001f');
    }, [params, paramsForSearchFields]);

    const hasScopeForEmptySearch = () => {
        if (!paramsForSearchFields || !params) {
            return false;
        }
        return paramsForSearchFields.some((pf) => {
            const key = pf[0];
            const v = params[key];
            return v != null && String(v).trim() !== '';
        });
    };

    useEffect(() => {
        if (!options.length) setCounter(counter+1);
        else setCounter(0);
    }, [options]);

    useEffect(() => {
        if (counter === 5 && searchErrorText) {
            toast.error(searchErrorText);
            setCounter(0);
        }
    }, [counter]);

    const searchDataFunc = (text, setFirst) => {
        const trimmed = text == null ? '' : String(text);
        const canEmpty = allowEmptySearch && hasScopeForEmptySearch();
        if (!trimmed && !canEmpty) {
            setOptions([]);
            return;
        }

        controller && controller.abort();
        controller = new AbortController();

        const fieldName = nameOption || name;

        const paramsData = {};

        paramsForSearchFields && paramsForSearchFields.forEach(p => {
            const nameParam = p[1] ? p[1] : p[0];
            paramsData[nameParam] = params[p[0]];
        })

        const pickCode = (d) => {
            if (d == null) return '';
            const v = d[fieldName];
            if (v != null && v !== '') return v;
            if (d.Code != null && d.Code !== '') return d.Code;
            if (d.code != null && d.code !== '') return d.code;
            return '';
        };

        apiPost(url, {
            ...paramsData,
            text: trimmed,
            sessionKey,
            maxCount
        }, controller.signal).then(data => {
            const rows = (data && data.entries) || [];
            const mapped = rows
                .filter((d) => pickCode(d))
                .map((d, index) => {
                    const pv = pickCode(d);
                    return {
                        value: pv,
                        key: String(pv) + '_' + index,
                        text: String(pv),
                        data: d,
                    };
                });

            const vCur = valueRef.current != null && String(valueRef.current).trim() !== ''
                ? String(valueRef.current).trim()
                : '';
            let list = mapped;
            if (vCur && !mapped.some((o) => String(o.value).toUpperCase() === vCur.toUpperCase())) {
                list = [
                    {
                        value: vCur,
                        key: '_selected_' + vCur,
                        text: vCur,
                        data: { [fieldName]: vCur, code: vCur, Code: vCur },
                    },
                    ...mapped,
                ];
            }
            setOptions(list);
            (setFirst && trimmed && rows.length) && onChangeValue(null, {value: pickCode(rows[0])});
            controller = null;
        }).catch((e) => {
            console.log('Отмена запроса - ', e)
        })
    }

    const searchData = useCallback(throttle(searchDataFunc, 600), [paramsScopeKey, paramsForSearchFields, allowEmptySearch, name, nameOption, url, maxCount]);

    const onChangeValue = (e, {value: selected}) => {
        const v = options.find((o) => String(o.value).toUpperCase() === String(selected).toUpperCase())
            || options.find((o) => o.value === selected);
        let values = {};
        if (v) {
            if (ignoredFields || !dependentFields) Object.keys(v.data).forEach(k => {
                if (!((ignoredFields || []).includes(k))) values[k] = v.data[k];
            });
            (dependentFields || []).forEach(f => values[f] = v.data[f] || null);
            onChange(nameOption || name, v.value, values);
        }
    };

    useEffect(() => {
        setSearchQuery(value || '');
    }, [value]);

    useEffect(() => {
        const canEmpty = allowEmptySearch && hasScopeForEmptySearch();
        if (searchQuery && searchQuery !== '') {
            searchData(searchQuery, value === searchQuery);
        } else if (canEmpty) {
            searchData('', false);
        } else {
            setOptions([]);
        }
    }, [searchQuery, paramsScopeKey, allowEmptySearch]);

    return (
        <Dropdown
            error={error}
            className={`selection input-dropdown ${className || ''}`}
            placeholder={placeholder}
            fluid
            name={name}
            search={() => options}
            searchQuery={searchQuery}
            onSearchChange={(e, {searchQuery: q}) => {
                setSearchQuery(q);
                if (!q && !allowEmptySearch) {
                    onChange(name, null);
                }
            }}
            disabled={disabled}
            options={options}
            value={value}
            noResultsMessage={'Не найдено'}
            onChange={onChangeValue}
            selectOnNavigation={false}
            selectOnBlur={false}
            onBlur={() => setSearchQuery(value || '')}
            onOpen={() => {
                if (disabled || !allowEmptySearch || !hasScopeForEmptySearch()) {
                    return;
                }
                searchDataFunc('', false);
            }}
        />
    );
};

export default InputDropdown;



// WEBPACK FOOTER //
// ./App/components/Dropdowns/InputDropdown.jsx